'use client'

import { useState } from 'react'
import { useCurrency } from '@/lib/currency'
import { payoutApi } from '@/lib/api'
import { X, AlertCircle, CreditCard, Building2, CheckCircle2, Clock } from 'lucide-react'

interface PayoutRequestModalProps {
  availableBalance: number
  kycVerified: boolean
  onClose: () => void
  onSuccess: () => void
}

export default function PayoutRequestModal({
  availableBalance,
  kycVerified,
  onClose,
  onSuccess
}: PayoutRequestModalProps) {
  const { format } = useCurrency()
  const [amount, setAmount] = useState('')
  const [paymentMethod, setPaymentMethod] = useState<'stripe_connect' | 'bank_transfer'>('bank_transfer')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  
  // Bank details
  const [bankDetails, setBankDetails] = useState({
    bankName: '',
    accountName: '',
    accountNumber: '',
    routingNumber: '',
    swiftCode: '',
    country: 'NG'
  })

  const MIN_PAYOUT = 50000
  const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    
    if (!token) {
      setError('Authentication required')
      return
    }

    const payoutAmount = parseFloat(amount)
    
    if (isNaN(payoutAmount) || payoutAmount < MIN_PAYOUT) {
      setError(`Minimum payout amount is ${format(MIN_PAYOUT)}`)
      return
    }
    
    if (payoutAmount > availableBalance) {
      setError(`Amount exceeds available balance of ${format(availableBalance)}`)
      return
    }
    
    if (!kycVerified) {
      setError('KYC verification is required to request payouts')
      return
    }

    if (paymentMethod === 'bank_transfer') {
      if (!bankDetails.bankName || !bankDetails.accountName || !bankDetails.accountNumber) {
        setError('Please fill in all required bank details')
        return
      }
    }

    setLoading(true)
    
    try {
      const response = await payoutApi.createRequest(token, {
        amount: payoutAmount,
        paymentMethod,
        bankDetails: paymentMethod === 'bank_transfer' ? bankDetails : undefined
      })
      
      if (response.success) {
        setSuccess(true)
      } else {
        setError(response.error || 'Failed to create payout request')
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create payout request')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-stone-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Request Payout</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        {success ? (
          <div className="p-6 space-y-6">
            <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-6 text-center">
              <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-emerald-900 mb-2">Payout request submitted</h3>
              <p className="text-emerald-800 mb-4">
                Your payout request has been received successfully.
              </p>
              <div className="bg-white/80 border border-emerald-200 rounded-lg p-4 flex items-start gap-3 text-left">
                <Clock className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-emerald-900">Security verification</p>
                  <p className="text-sm text-emerald-800">
                    Your payment will be processed within <strong>12 hours</strong> for security verification. You will be notified once the transfer is completed.
                  </p>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                onSuccess()
                onClose()
              }}
              className="w-full py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Available Balance */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-emerald-900">Available Balance</span>
              <span className="text-2xl font-bold text-emerald-600">{format(availableBalance)}</span>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* KYC Warning */}
          {!kycVerified && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <p className="text-sm text-amber-700">
                KYC verification is required before you can request payouts. Please complete your KYC verification first.
              </p>
            </div>
          )}

          {/* Amount Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payout Amount <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min={MIN_PAYOUT}
              max={availableBalance}
              step="1000"
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder={`Minimum: ${format(MIN_PAYOUT)}`}
            />
            <p className="text-xs text-gray-500 mt-1">
              Minimum payout: {format(MIN_PAYOUT)} | Maximum: {format(availableBalance)}
            </p>
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Method <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setPaymentMethod('bank_transfer')}
                className={`p-4 border-2 rounded-lg transition-all ${
                  paymentMethod === 'bank_transfer'
                    ? 'border-emerald-600 bg-emerald-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Building2 className={`w-6 h-6 mb-2 ${paymentMethod === 'bank_transfer' ? 'text-emerald-600' : 'text-gray-400'}`} />
                <p className="font-medium text-gray-900">Bank Transfer</p>
                <p className="text-xs text-gray-500 mt-1">Direct bank transfer</p>
              </button>
              
              <button
                type="button"
                onClick={() => setPaymentMethod('stripe_connect')}
                className={`p-4 border-2 rounded-lg transition-all ${
                  paymentMethod === 'stripe_connect'
                    ? 'border-emerald-600 bg-emerald-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <CreditCard className={`w-6 h-6 mb-2 ${paymentMethod === 'stripe_connect' ? 'text-emerald-600' : 'text-gray-400'}`} />
                <p className="font-medium text-gray-900">Stripe Connect</p>
                <p className="text-xs text-gray-500 mt-1">Stripe account (Coming soon)</p>
              </button>
            </div>
          </div>

          {/* Bank Details */}
          {paymentMethod === 'bank_transfer' && (
            <div className="space-y-4 border-t border-gray-200 pt-4">
              <h3 className="font-medium text-gray-900">Bank Details</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bank Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={bankDetails.bankName}
                    onChange={(e) => setBankDetails({ ...bankDetails, bankName: e.target.value })}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="e.g., Access Bank"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Account Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={bankDetails.accountName}
                    onChange={(e) => setBankDetails({ ...bankDetails, accountName: e.target.value })}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Account holder name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Account Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={bankDetails.accountNumber}
                    onChange={(e) => setBankDetails({ ...bankDetails, accountNumber: e.target.value })}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="10-digit account number"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Routing Number (Optional)
                  </label>
                  <input
                    type="text"
                    value={bankDetails.routingNumber}
                    onChange={(e) => setBankDetails({ ...bankDetails, routingNumber: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Bank routing number"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SWIFT Code (Optional)
                  </label>
                  <input
                    type="text"
                    value={bankDetails.swiftCode}
                    onChange={(e) => setBankDetails({ ...bankDetails, swiftCode: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Bank SWIFT code"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country
                  </label>
                  <input
                    type="text"
                    value={bankDetails.country}
                    onChange={(e) => setBankDetails({ ...bankDetails, country: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Note about commission */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Commission has already been deducted from your available balance. 
              The amount you request will be the net amount (after commission) that you'll receive.
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !kycVerified || availableBalance < MIN_PAYOUT}
              className="px-6 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {loading ? 'Processing...' : 'Request Payout'}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  )
}

